import React, { useEffect, useState } from "react";
import { Form, Input, Select, Upload, Button, message, Modal } from "antd";
import useJobStore from "../zustand/store";
import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaDollarSign,
} from "react-icons/fa";
import axios from "axios";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
const { TextArea } = Input;
const { Option } = Select;
const ApplicationForm = ({ job, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  // Handle file changes (upload/delete)
  const handleFileChange = (info) => {
    let newFileList = [...info.fileList];

    // Limit to only 1 file by keeping the latest one
    newFileList = newFileList.slice(-1);

    // File type validation
    newFileList = newFileList.filter((file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(file.type);

      if (!isValidType && file.status !== "error") {
        message.error(
          `${file.name} is not a valid file type. Please upload PDF or DOC/DOCX files.`
        );
        return false;
      }

      // File size validation (10MB limit)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M && file.status !== "error") {
        message.error("File must be smaller than 10MB!");
        return false;
      }

      return true;
    });

    setFileList(newFileList);

    // Important: Update form field value
    form.setFieldsValue({ resume: newFileList });
  };

  // Normalize file value for form
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList || [];
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (key === "resume" && values[key]) {
          const file = values[key][0]?.originFileObj;
          if (file) {
            formData.append("resume", file);
          }
        } else {
          // Set fallback values for optional fields
          const fallbackFields = ["linkedinProfile", "reason"];
          const value =
            values[key] || (fallbackFields.includes(key) ? "NA" : "");
          formData.append(key, value);
        }
      });
      let response;
      // Send formData to careers endpoint
      response = await axios.post("/user/careers", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Success flow
      if (response) {
        toast.success("Form submitted successfully!!", { autoClose: 3000 });
        form.resetFields();
        setFileList([]);
      }
      onClose();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Validation rules for form fields
  const validationRules = {
    firstName: [
      { required: true, message: "Enter your first name" },
      {
        pattern: /^[A-Za-z\s'-]{2,}$/,
        message: "Must be at least 2 characters. Only letters, spaces allowed",
      },
    ],
    lastName: [
      { required: true, message: "Please enter your last name" },
      {
        pattern: /^[A-Za-z\s'-]{2,}$/,
        message: "Must be at least 2 characters. Only letters, spaces allowed",
      },
    ],
    email: [
      { required: true, message: "Please enter your email" },
      { type: "email", message: "Please enter a valid email" },
    ],
    phone: [
      { required: true, message: "Enter phone number" },
      {
        pattern: /^\d{6,15}$/,
        message: "Phone number must be 6 to 15 digits long as per your country",
      },
    ],
    yearsOfExperience: [
      { required: true, message: "Please select your experience" },
    ],
    resume: [{ required: true, message: "Please upload your resume" }],
    countryCode: [
      { required: true, message: "Enter country code" },
      { pattern: /^\+\d+$/, message: "Valid format: +1, +966, etc." },
    ],
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        jobTitle: job?.title,
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Form.Item
          label="First Name"
          name="firstName"
          rules={validationRules.firstName}
        >
          <Input placeholder="Enter your first name" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="lastName"
          rules={validationRules.lastName}
        >
          <Input placeholder="Enter your last name" />
        </Form.Item>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Form.Item label="Email" name="email" rules={validationRules.email}>
          <Input placeholder="Enter your email address" />
        </Form.Item>

        <div className="grid grid-cols-3 gap-2">
          <Form.Item
            label={<label className="text-[11px]">Country Code</label>}
            name="countryCode"
            rules={validationRules.countryCode}
          >
            <Input
              placeholder="+966"
              maxLength={5}
              onKeyPress={(e) => {
                if (!/[+\d]/.test(e.key)) e.preventDefault();
              }}
            />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone"
            className="col-span-2"
            rules={validationRules.phone}
          >
            <Input
              placeholder="Enter your phone number"
              maxLength={15}
              onKeyPress={(e) => {
                if (!/\d/.test(e.key)) e.preventDefault();
              }}
            />
          </Form.Item>
        </div>
      </div>

      <Form.Item
        label="LinkedIn Profile (Optional)"
        name="linkedinProfile"
        rules={[{ type: "url", message: "Please enter a valid URL" }]}
      >
        <Input placeholder="https://linkedin.com/in/yourprofile" />
      </Form.Item>

      <Form.Item
        label="Years of Experience"
        name="yearsOfExperience"
        rules={validationRules.yearsOfExperience}
      >
        <Select placeholder="Select your experience">
          <Option value="0-1">0-1 years</Option>
          <Option value="1-3">1-3 years</Option>
          <Option value="3-5">3-5 years</Option>
          <Option value="5-10">5-10 years</Option>
          <Option value="10+">10+ years</Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="resume"
        label={<span className="text-white">Resume</span>}
        valuePropName="fileList"
        getValueFromEvent={normFile}
        rules={[
          {
            required: true,
            message: "Please upload your resume",
          },
        ]}
      >
        <div className="space-y-4">
          <Upload
            name="resume"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false}
            accept=".pdf,.doc,.docx"
            maxCount={1}
            onRemove={() => setFileList([])}
            showUploadList={{
              showRemoveIcon: true,
              removeIcon: (
                <DeleteOutlined
                  style={{ color: "black" }}
                  className="text-red-500 hover:text-red-700"
                />
              ),
            }}
          >
            <Button icon={<UploadOutlined />}>Upload Resume</Button>
          </Upload>

          {/* File information */}
          {fileList.length > 0 && (
            <div className="text-gray-500 text-sm">
              Current file: {fileList[0].name}
            </div>
          )}

          {/* File requirements */}
          <div className="text-xs text-gray-500">
            Supported formats: PDF, DOC, DOCX (Max size: 10MB)
          </div>
        </div>
      </Form.Item>

      <Form.Item label="Reason for Interest" name="reason">
        <TextArea
          rows={4}
          placeholder="Tell us why you're interested in this position..."
        />
      </Form.Item>

      <Form.Item className="flex justify-end space-x-4">
        <Button className="mr-10" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {loading ? "Submitting..." : "Submit Application"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const { jobData, loadingAPI, error, fetchJobs } = useJobStore();
  const [selectedJobForApplication, setSelectedJobForApplication] =
    useState(null);
  useEffect(() => {
    if (jobData.length === 0) {
      fetchJobs();
    }
  }, [jobData, fetchJobs]);

  const handleApplyNow = (job) => {
    setSelectedJobForApplication(job);
    setShowApplicationForm(true);
  };
  return (
    <div className="min-h-screen">
      <div className="text-white bg-gradient-to-b from-[#6764AD] via-[#915BA6] to-[#202020]">
        <div className="container mx-auto px-4 pt-20 pb-2">
          <div className="max-w-3xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              data-aos="fade-up"
            >
              Join Our Team
            </h1>
            <p
              className="text-xl text-blue-100 mb-8"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Build the future with us. Discover exciting opportunities to grow
              your career.
            </p>
          </div>
        </div>
      </div>
      <div className="pt-4 bg-gradient-to-b from-[#6764AD] via-[#915BA6] to-[#202020]">
        {/* Jobs Section */}
        <div className="container mx-auto px-4 pb-16">
          <div className="max-w-6xl mx-auto">
            {/* Job Filters (can be expanded) */}
            <div className="mb-12 flex flex-wrap gap-4" data-aos="fade-up">
              <button className="px-6 py-2 bg-black text-white font-bold rounded-full hover:bg-blue-700 transition-colors">
                All Positions
              </button>
              {/* <button className="px-6 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors">
              Engineering
            </button>
            <button className="px-6 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors">
              Design
            </button> */}
              {/* Add more filter buttons as needed */}
            </div>

            {/* Job Listings */}
            <div className="grid gap-6">
              {jobData.length > 0 ? (
                jobData.map((job) => (
                  <div
                    key={job._id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
                    data-aos="fade-up"
                  >
                    <div className="p-6">
                      <div className="flex flex-wrap justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                            {job.title}
                          </h2>
                          <div className="flex flex-wrap gap-4 text-gray-600 mb-4">
                            <div className="flex items-center">
                              <FaMapMarkerAlt className="mr-2 text-blue-600" />
                              {job.location}
                            </div>
                            <div className="flex items-center">
                              <FaClock className="mr-2 text-blue-600" />
                              {job.type}
                            </div>
                            <div className="flex items-center">
                              <FaBriefcase className="mr-2 text-blue-600" />
                              {job.experience}
                            </div>
                            <div className="flex items-center">
                              <FaDollarSign className="mr-2 text-blue-600" />
                              {job.salary}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            setSelectedJob(
                              selectedJob === job._id ? null : job._id
                            )
                          }
                          className="mt-4 md:mt-0 px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                        >
                          {selectedJob === job._id ? "View Less" : "View More"}
                        </button>
                      </div>

                      {selectedJob === job._id && (
                        <div className="mt-6 border-t pt-6">
                          <p className="text-gray-700 mb-6">
                            {job.description}
                          </p>

                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                              Key Responsibilities
                            </h3>
                            <ul className="space-y-2">
                              {job.responsibilities.map((item, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                                  <span className="text-gray-600">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">
                              Requirements
                            </h3>
                            <ul className="space-y-2">
                              {job.requirements.map((item, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3"></span>
                                  <span className="text-gray-600">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <button
                            onClick={() => handleApplyNow(job)}
                            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                          >
                            Apply Now
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {error ? (
                    <h1 className="text-white text-center text-xl">
                      Unexpected error occured while fetching Jobs
                    </h1>
                  ) : (
                    <LoadingJobs />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2
                className="text-3xl font-bold text-white mb-4"
                data-aos="fade-up"
              >
                Life @ Solo Source
              </h2>
              <p
                className="text-gray-300"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                We offer more than just a job. Join us and enjoy these benefits.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  title: "Competitive Salary",
                  description:
                    "We offer industry-leading compensation packages",
                  icon: "💰",
                },
                {
                  title: "Remote Work",
                  description:
                    "Enjoy flexible work arrangements, including remote opportunities",
                  icon: "🏠",
                },
                {
                  title: "Growth Opportunities",
                  description:
                    "We support continuous learning and career development",
                  icon: "📈",
                },
                {
                  title: "Health Benefits",
                  description: "Comprehensive health and wellness coverage",
                  icon: "🏥",
                },
                {
                  title: "Work-Life Balance",
                  description: "Flexible hours and generous paid time off",
                  icon: "⚖️",
                },
                {
                  title: "Team Events",
                  description:
                    "Team-building and social activities to keep things fun",
                  icon: "🎉",
                },
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-6 rounded-xl text-center"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="text-4xl mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <Modal
          open={showApplicationForm}
          onCancel={() => setShowApplicationForm(false)}
          footer={null}
          width={700}
          destroyOnClose
          bodyStyle={{
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          <ApplicationForm
            job={selectedJobForApplication}
            onClose={() => setShowApplicationForm(false)}
          />
        </Modal>
      </div>
    </div>
  );
}
const LoadingJobs = () => {
  return (
    <div className="text-white text-3xl text-center py-12">
      <h1 className="flex justify-center items-center gap-1">
        Loading Jobs
        <span className="flex gap-1 ml-2">
          <span
            className="animate-bounce text-5xl"
            style={{ animationDelay: "0ms" }}
          >
            .
          </span>
          <span
            className="animate-bounce text-5xl"
            style={{ animationDelay: "200ms" }}
          >
            .
          </span>
          <span
            className="animate-bounce text-5xl"
            style={{ animationDelay: "400ms" }}
          >
            .
          </span>
        </span>
      </h1>
    </div>
  );
};
